import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Check, Camera, Video, Sparkles, Users, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

import gallery3 from "@/assets/gallery-3.jpg";

interface Package {
  id: string;
  name: string;
  price: number;
  features: string[];
  is_featured: boolean;
  description: string | null;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  description: string | null;
}

const ComboPackages = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState({ url: gallery3, alt: "Wedding couple" });

  useEffect(() => {
    const fetchData = async () => {
      const [packagesResult, addonsResult] = await Promise.all([
        supabase
          .from("pricing_packages")
          .select("*")
          .eq("is_active", true)
          .eq("category", "bundle")
          .order("sort_order"),
        supabase
          .from("pricing_addons")
          .select("*")
          .eq("is_active", true)
          .eq("category", "bundle")
          .order("sort_order"),
      ]);

      if (packagesResult.data) {
        const sorted = [...packagesResult.data].sort((a, b) => b.price - a.price);
        const featuredIdx = sorted.findIndex(p => p.is_featured);
        if (featuredIdx !== -1 && featuredIdx !== 1 && sorted.length > 1) {
          const [featured] = sorted.splice(featuredIdx, 1);
          sorted.splice(1, 0, featured);
        }
        setPackages(sorted);
      }
      if (addonsResult.data) setAddons(addonsResult.data);

      try {
        const { data: locations } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "combo_packages_page");

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          const { data: mediaData } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(1);

          if (mediaData && mediaData.length > 0) {
            setHeroImage({
              url: mediaData[0].url,
              alt: mediaData[0].alt_text || mediaData[0].title,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching portfolio media:", error);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);

  const getPackageIcon = (name: string) => {
    if (name.toLowerCase().includes("photo") && name.toLowerCase().includes("video")) {
      return <><Camera className="h-5 w-5" /><Video className="h-5 w-5" /></>;
    }
    if (name.toLowerCase().includes("photo")) {
      return <Camera className="h-5 w-5" />;
    }
    if (name.toLowerCase().includes("video")) {
      return <Video className="h-5 w-5" />;
    }
    return <Sparkles className="h-5 w-5" />;
  };

  const valueProps = [
    {
      icon: Sparkles,
      title: "Save More",
      description: "Bundled services mean better prices compared to booking separately.",
    },
    {
      icon: Users,
      title: "Seamless Experience",
      description: "One team, one vision. Coordinated coverage for a cohesive story.",
    },
    {
      icon: Heart,
      title: "Complete Coverage",
      description: "Photos, video, and photo booth - capture every angle of your day.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Combo Wedding Packages | Photo + Video Bundles | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Save with bundled wedding packages. Combine photography, cinematography & photo booth for complete coverage. Serving NJ, FL & CO."
        />
        <meta property="og:title" content="Combo Wedding Packages | Photo + Video Bundles | Pixie Clicks" />
        <meta property="og:description" content="Save with bundled wedding packages. Complete coverage with photography, cinematography & photo booth." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/combo-packages" />
        <link rel="canonical" href="https://pixieclicks.com/combo-packages" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Combo Wedding Packages",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            },
            "description": "Bundled wedding packages combining photography, cinematography, and photo booth services."
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32 bg-muted/30">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-primary">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-medium">Best Value</span>
                </div>
                <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
                  Better <span className="text-primary">Together</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Combine our services and save. Our bundled packages offer complete wedding 
                  coverage at exceptional value, ensuring every moment is captured beautifully.
                </p>
                <Button variant="hero" size="lg" className="mt-8" asChild>
                  <Link to="/contact">Get a Custom Quote</Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src={heroImage.url}
                  alt={heroImage.alt}
                  className="rounded-2xl shadow-elevated"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Bundle */}
        <section className="section-padding">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Why Bundle Your Services
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {valueProps.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section-padding bg-muted/30">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Bundled Packages
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
              Choose the perfect combination for your wedding day
            </p>

            {loading ? (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-96 animate-pulse rounded-xl bg-muted"
                  />
                ))}
              </div>
            ) : (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative flex flex-col rounded-xl border p-6 ${
                      pkg.is_featured
                        ? "border-primary bg-primary/5 shadow-rose"
                        : "border-border bg-card"
                    }`}
                  >
                    {pkg.is_featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                        Most Popular
                      </span>
                    )}
                    <div className="flex items-center gap-2 text-primary mb-4">
                      {getPackageIcon(pkg.name)}
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-foreground">
                      {pkg.name}
                    </h3>
                    <p className="mt-2 text-3xl font-bold text-primary">
                      {formatPrice(pkg.price)}
                    </p>
                    {pkg.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {pkg.description}
                      </p>
                    )}
                    <ul className="mt-6 flex-1 space-y-2">
                      {pkg.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-sm"
                        >
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={pkg.is_featured ? "hero" : "outline"}
                      className="mt-6 w-full"
                      asChild
                    >
                      <Link to={`/contact?package=${encodeURIComponent(pkg.name)}`}>Check Availability</Link>
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Add-ons */}
        {addons.length > 0 && (
          <section className="section-padding">
            <div className="container-wide">
              <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
                Make It Yours
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
                Add these extras to make your wedding coverage even more special
              </p>
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {addons.map((addon) => (
                  <div
                    key={addon.id}
                    className="flex flex-col rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-medium text-foreground">{addon.name}</h3>
                      <p className="text-xl font-bold text-primary">
                        {formatPrice(addon.price)}
                      </p>
                    </div>
                    {addon.description && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {addon.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Ready to Bundle & Save?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Let's create a custom package that perfectly fits your wedding vision and budget.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="lg" asChild>
                <Link to="/contact">Get Your Custom Quote</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/galleries">View Our Work</Link>
              </Button>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default ComboPackages;
